import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { formatCurrency } from '@/libs/antd/formater';
import {
	ShareholderSlikInformation,
	SlikRobotikDetail,
	SlikRobotikResult,
	SlikRobotikResultDetail,
	SlikRobotikUserRoles,
} from '@/libs/fleet-api/services/prospect';
import {
	ArrowRightOutlined,
	DownloadOutlined,
	FileSearchOutlined,
} from '@ant-design/icons';
import { Alert, Collapse, Divider, Table, Tabs, Typography } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useMemo, useState } from 'react';
import { exportToExcel } from 'react-json-to-excel';

const formatCBASDate = (date: string) => {
	if (!date || !date.length) return '-';
	return dayjs(date).format('DD/MM/YYYY');
};

const CBAS_URL = import.meta.env.VITE_FLEET_CBAS_URL;

const AnalisaSLIKFormRobotik = ({
	shareholderSlikInformation,
	managementSlikInformation,
	data,
	result,
}: {
	shareholderSlikInformation: ShareholderSlikInformation[];
	managementSlikInformation: ShareholderSlikInformation[];
	data: SlikRobotikDetail;
	result?: SlikRobotikResult['details'];
}) => {
	const [selectedDetail, setSelectedDetail] = useState<{
		referenceNumber: string;
		detail: 'management' | 'shareholder';
		type: 'facility' | 'reporter';
	}>();

	const [activeKey, setActiveKey] = useState<string>('reporter');

	const selectedIndexes = useMemo(() => {
		const indexes: SlikRobotikResultDetail[] = [];
		if (result) {
			indexes.push(...result.filter((result) => result?.selectedIndexes));
		}
		return indexes;
	}, [result]);

	// Selected Companies
	const selectedCompaniesIndexes = useMemo(() => {
		return (
			selectedIndexes
				?.filter((index) => index.role === 'Customers')[0]
				?.selectedIndexes?.split(',')
				.map(Number) ?? [0]
		);
	}, [selectedIndexes]);

	// Selected Shareholder Companies
	const selectedShareholderCompaniesIndexes = useMemo(() => {
		const shareholderIndexes = selectedIndexes.filter(
			(index) =>
				index.role === 'CustomerCompanyManagementShareholderInformation' ||
				index.role === 'CustomerCompanyShareholderInformation',
		);
		const indexes: { referenceNumber: string; secondIndex: number[] }[] = [];
		if (shareholderIndexes.length > 0) {
			shareholderIndexes.forEach((index) => {
				indexes.push({
					referenceNumber: index.referenceNumber,
					secondIndex: index.selectedIndexes?.split(',').map(Number) ?? [0],
				});
			});
		}
		return indexes;
	}, [selectedIndexes]);

	// const [
	// 	selectedShareholderCompaniesIndexes,
	// 	setSelectedShareholderCompaniesIndexes,
	// ] = useState<{ secondIndex: number; referenceNumber: string }[]>([]);

	const downloadExcel = (
		fileName: string,
		dataSource1: { sheetName: string; data: Record<string, unknown> },
		dataSource2: { sheetName: string; data: Record<string, unknown> },
	) => {
		const json = [
			{ sheetName: dataSource1.sheetName, details: dataSource1.data },
			{ sheetName: dataSource2.sheetName, details: dataSource2.data },
		];

		exportToExcel(json, fileName, true);
	};

	const companyFacilityDataSource = useMemo(
		() =>
			// Select Pelapor Summary
			data?.[0]?.VW_RPT_DETAIL_FASILITAS.filter((record) => {
				const selectedCompanies = data?.[0]?.RPT_SUMMARY_COMPANY.filter(
					(_, i) => selectedCompaniesIndexes.includes(i),
				);

				// Check if the record's identitasnew matches any of the selected companies' npwp
				return selectedCompanies.some(
					(company) => company.npwp === record.identitasnew,
				);
			}),
		[data, selectedCompaniesIndexes],
	);

	const companyReporterDataSource = useMemo(
		() =>
			// Select Pelapor Summary
			data?.[0]?.VW_RPT_SUMMARY_PEL.filter((record) => {
				const selectedCompanies = data?.[0]?.RPT_SUMMARY_COMPANY.filter(
					(_, i) => selectedCompaniesIndexes.includes(i),
				);

				// Check if the record's identitasnew matches any of the selected companies' npwp
				return selectedCompanies.some(
					(company) => company.npwp === record.identitasnew,
				);
			}),
		[data, selectedCompaniesIndexes],
	);

	const managementSummaries = result
		?.filter((user) =>
			data
				// skip view company
				.slice(1)
				// filter those who are management
				.some(
					() =>
						user.role === SlikRobotikUserRoles.MANAGEMENT ||
						user.role === SlikRobotikUserRoles.MANAGEMENT_SHAREHOLDER,
				),
		)
		.map((user, index) => {
			const matchingItem = data.find(
				(item) => user.referenceNumber === item.ReferenceNumber,
			);
			if (user.status === 'NotFound') {
				const dataManagement = managementSlikInformation.find(
					(item) => user.managementId === item.id.toString(),
				);

				return {
					key: `pengurus${index}`,
					label: `Ringkasan Pengurus ${index + 1}`,
					children: (
						<Fragment>
							<Alert
								message="Proses pengecekan SLIK sudah dilakukan dan data debitur tidak ditemukan."
								type="warning"
								className="custom-alert-info"
							/>
							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={[
									(dataManagement as ShareholderSlikInformation) ?? {},
								]}
								columns={[
									{
										title: 'View Ideb',
										width: 100,
										align: 'center',
										render: () => (
											<span style={{ cursor: 'not-allowed', color: '#bababa' }}>
												<FileSearchOutlined />
											</span>
										),
									},
									{
										title: 'Download File',
										render: () => (
											<span style={{ cursor: 'not-allowed', color: '#bababa' }}>
												<DownloadOutlined />
											</span>
										),
									},
									{ title: 'Nama Debitur', render: (_, record) => record.name },
									{
										title: 'Identity Number',
										render: (_, record) => record.identityNumber,
									},

									{ title: 'Tanggal Update Terakhir', render: () => '-' },
									{ title: 'DOB', render: () => '-' },
									{ title: 'NIK', render: () => '-' },
									{ title: 'Nama Ibu Kandung', render: () => '-' },
									{ title: 'Jenis Kelamin', render: () => '-' },
									{ title: 'Alamat', render: () => '-' },
									{ title: 'Pekerjaan', render: () => '-' },
									{ title: 'Tempat Bekerja', render: () => '-' },
								]}
							/>
						</Fragment>
					),
				};
			}

			return {
				key: `pengurus${index}`,
				label: `Ringkasan Pengurus ${index + 1}`,
				children: (
					<Fragment>
						<Table
							scroll={{ x: 'max-content' }}
							style={{ marginBottom: 20 }}
							pagination={false}
							dataSource={matchingItem?.RPT_SUMMARY_PERSONAL || []}
							columns={[
								{
									title: 'View Ideb',
									width: 100,
									align: 'center',
									render: (_, record) => (
										<a
											href={CBAS_URL}
											target="_blank"
										>
											<FileSearchOutlined
												style={
													!('reffnumber' in record) ? { color: '#bababa' } : {}
												}
											/>
										</a>
									),
								},
								{
									title: 'Download File',
									render: () => (
										<a
											href="#"
											onClick={async (e) => {
												e.preventDefault();
												downloadExcel(
													matchingItem?.RPT_SUMMARY_PERSONAL[0].namaDebitur ??
														'',
													{
														sheetName: 'Per Pelapor',
														data: matchingItem?.VW_RPT_SUMMARY_PEL as unknown as Record<
															string,
															unknown
														>,
													},
													{
														sheetName: 'Per Pemegang Saham',
														data: matchingItem?.VW_RPT_DETAIL_FASILITAS as unknown as Record<
															string,
															unknown
														>,
													},
												);
											}}
										>
											<DownloadOutlined />
										</a>
									),
								},
								{
									title: 'Nama Debitur',
									render: (_, record) => record.namaDebitur ?? '-',
								},
								{
									title: 'Tanggal Update Terakhir',
									render: (_, record) => formatCBASDate(record.tanggalUpdate),
								},
								{
									title: 'DOB',
									render: (_, record) => formatCBASDate(record.tanggalLahir),
								},
								{ title: 'NIK', render: (_, record) => record.ktp ?? '-' },
								{
									title: 'Nama Ibu Kandung',
									render: (_, record) => record.mmname ?? '-',
								},
								{
									title: 'Jenis Kelamin',
									render: (_, record) => record.jenisKelaminKet ?? '-',
								},
								{
									title: 'Alamat',
									render: (_, record) => record.alamat ?? '-',
								},
								{
									title: 'Pekerjaan',
									render: (_, record) => record.pekerjaanKet ?? '-',
								},
								{
									title: 'Tempat Bekerja',
									render: (_, record) => record.tempatBekerja ?? '-',
								},
							]}
						/>
						<Divider />

						<Typography.Title level={3}>Ringkasan Fasilitas</Typography.Title>
						<Table
							scroll={{ x: 'max-content' }}
							style={{ marginBottom: 20 }}
							pagination={false}
							dataSource={(matchingItem?.RPT_SUMMARY_PERSONAL || []).flatMap(
								(record) => [
									{ ...record, rowType: 'plafonEfektif' },
									{ ...record, rowType: 'bakiDebet' },
								],
							)}
							columns={[
								{
									title: 'Fasilitas',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? 'Plafon Efektif'
											: 'Baki Debet',
								},
								{
									title: 'Kredit/Pembiayaan (dalam IDR)',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? formatCurrency(Number(record.plafonEfektifKredit)) ??
												'-'
											: formatCurrency(Number(record.bakiDebetKredit)) ?? '-',
								},
								{
									title: 'Surat Berharga (dalam IDR)',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? formatCurrency(Number(record.plafonEfektifSec)) ?? '-'
											: formatCurrency(Number(record.bakiDebetSec)) ?? '-',
								},
								{
									title: 'Irrevocable L/C (dalam IDR)',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? formatCurrency(Number(record.plafonEfektifLc)) ?? '-'
											: formatCurrency(Number(record.bakiDebetLc)) ?? '-',
								},
								{
									title: 'Garansi yang diberikan (dalam IDR)',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? formatCurrency(Number(record.plafonEfektifBg)) ?? '-'
											: formatCurrency(Number(record.bakiDebetBg)) ?? '-',
								},
								{
									title: 'Fasilitas Lain (dalam IDR)',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? formatCurrency(Number(record.plafonEfektifLainnya)) ??
												'-'
											: formatCurrency(Number(record.bakiDebetLainnya)) ?? '-',
								},
								{
									title: 'Total (dalam IDR)',
									render: (_, record) =>
										record.rowType === 'plafonEfektif'
											? formatCurrency(Number(record.plafonEfektifTotal)) ?? '-'
											: formatCurrency(Number(record.bakiDebetTotal)) ?? '-',
								},
							]}
						/>

						<Divider />

						<Table
							scroll={{ x: 'max-content' }}
							style={{ marginBottom: 20 }}
							pagination={false}
							dataSource={[
								[
									{
										title: 'Lembaga Pembiayaan',
										value:
											matchingItem?.RPT_SUMMARY_PERSONAL?.[0]?.krediturLp ??
											'0',
									},
									{
										title: 'Lainnya',
										value:
											matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
												?.krediturLainnya ?? '0',
									},
								],
								[
									{
										title: 'BPR/BPRS',
										value:
											matchingItem?.RPT_SUMMARY_PERSONAL?.[0]?.krediturBPR_S ??
											'0',
									},
									{
										title: 'Bank Umum',
										value:
											matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
												?.krediturBankUmum ?? '0',
									},
								],
							]}
							columns={[
								{
									title: 'Jumlah Kreditur',
									children: [
										{
											children: [
												{ render: (_, record) => record[0].title, width: 100 },
												{ render: (_, record) => record[0].value, width: 100 },
											],
										},
										{
											children: [
												{ render: (_, record) => record[1].title, width: 100 },
												{ render: (_, record) => record[1].value, width: 100 },
											],
										},
									],
								},
							]}
						/>

						<Divider />

						<Table
							scroll={{ x: 'max-content' }}
							style={{ marginBottom: 20 }}
							pagination={false}
							dataSource={[
								[
									{
										title: 'Kualitas Terburuk',
										value:
											matchingItem?.RPT_SUMMARY_PERSONAL?.[0]?.kolekTerburuk ??
											'-',
									},
									{
										title: 'Periode',
										value:
											formatCBASDate(
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
													?.kolekBulanDataTerburuk || '',
											) ?? '-',
									},
								],
								[
									{
										title: 'Kualitas Terakhir',
										value:
											matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
												?.tahunBulanKolAkhir ?? '-',
									},
									{
										title: 'Periode Terakhir',
										value:
											formatCBASDate(
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]?.tahunBulan01 ||
													'',
											) ?? '-',
									},
								],
							]}
							columns={[
								{
									title: 'Kolektibilitas',
									children: [
										{
											children: [
												{ render: (_, record) => record[0].title, width: 100 },
												{ render: (_, record) => record[0].value, width: 100 },
											],
										},
										{
											children: [
												{ render: (_, record) => record[1].title, width: 100 },
												{ render: (_, record) => record[1].value, width: 100 },
											],
										},
									],
								},
							]}
						/>

						<Divider />

						<Typography.Title
							level={3}
							onClick={() =>
								setSelectedDetail({
									referenceNumber: matchingItem?.ReferenceNumber ?? '',
									detail: 'management',
									type: 'reporter',
								})
							}
						>
							<span style={{ marginRight: 10 }}>View Detail</span>
							<ArrowRightOutlined style={{ color: '#1557BC' }} />
						</Typography.Title>
					</Fragment>
				),
			};
		});

	const shareholders = result?.filter((user) =>
		data
			// skip view company
			.slice(1)
			// filter those who are shareholders
			.some(
				() =>
					user.role === SlikRobotikUserRoles.SHAREHOLDER ||
					user.role === SlikRobotikUserRoles.MANAGEMENT_SHAREHOLDER,
			),
	);

	const shareholderSummaries = shareholders?.map((user, index) => {
		const matchingItem = data.find(
			(item) => user.referenceNumber === item.ReferenceNumber,
		);

		if (user.status === 'NotFound') {
			const dataShareholder = shareholderSlikInformation.find(
				(item) => user.referrenceId === item.id.toString(),
			);

			return {
				key: `shareholder${index}`,
				label: `Ringkasan Pemegang Saham ${index + 1}`,
				children: (
					<Fragment>
						<Alert
							message="Proses pengecekan SLIK sudah dilakukan dan data debitur tidak ditemukan."
							type="warning"
							className="custom-alert-info"
						/>
						<Table
							scroll={{ x: 'max-content' }}
							style={{ marginBottom: 20 }}
							pagination={false}
							dataSource={[
								(dataShareholder as ShareholderSlikInformation) ?? {},
							]}
							columns={[
								{
									title: 'View Ideb',
									width: 100,
									align: 'center',
									render: () => (
										<span style={{ cursor: 'not-allowed', color: '#bababa' }}>
											<FileSearchOutlined />
										</span>
									),
								},
								{
									title: 'Download File',
									render: () => (
										<span style={{ cursor: 'not-allowed', color: '#bababa' }}>
											<DownloadOutlined />
										</span>
									),
								},
								{ title: 'Nama Debitur', render: (_, record) => record.name },
								{
									title: 'Identity Number',
									render: (_, record) => record.identityNumber,
								},

								{ title: 'Tanggal Update Terakhir', render: () => '-' },
								{ title: 'DOB', render: () => '-' },
								{ title: 'NIK', render: () => '-' },
								{ title: 'Nama Ibu Kandung', render: () => '-' },
								{ title: 'Jenis Kelamin', render: () => '-' },
								{ title: 'Alamat', render: () => '-' },
								{ title: 'Pekerjaan', render: () => '-' },
								{ title: 'Tempat Bekerja', render: () => '-' },
							]}
						/>
					</Fragment>
				),
			};
		}
		return {
			key: `shareholder${index}`,
			label: `Ringkasan Pemegang Saham ${index + 1}`,
			children: (
				<Fragment>
					{selectedShareholderCompaniesIndexes.some(
						(i) => i.referenceNumber === matchingItem?.ReferenceNumber,
					) ? (
						<Fragment>
							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={(() => {
									const foundIndices = (
										selectedShareholderCompaniesIndexes || []
									).filter(
										(i) => i.referenceNumber === matchingItem?.ReferenceNumber,
									);
									// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
									if (foundIndices.length > 0) {
										return foundIndices.flatMap((foundIndex) => {
											const dataEntry = data.find(
												(item) =>
													item.ReferenceNumber ===
													matchingItem?.ReferenceNumber,
											);
											if (dataEntry) {
												return (dataEntry.RPT_SUMMARY_COMPANY || []).filter(
													(_, secondIndex) =>
														foundIndex.secondIndex.includes(secondIndex),
												);
											}
											return []; // Return an empty array if dataEntry is undefined
										});
									}
									return []; // Return an empty array if no valid indices were found
								})()}
								columns={[
									{
										title: 'View Ideb',
										width: 100,
										align: 'center',
										render: (_, record) => (
											<a
												href={CBAS_URL}
												target="_blank"
											>
												<FileSearchOutlined
													style={
														!('reffnumber' in record)
															? { color: '#bababa' }
															: {}
													}
												/>
											</a>
										),
									},
									{
										title: 'Download File',
										render: (_, record) => (
											<a
												href="#"
												onClick={async (e) => {
													e.preventDefault();
													downloadExcel(
														record.namaDebitur,
														{
															sheetName: 'Per Pelapor',
															data: (() => {
																const foundIndices = (
																	selectedShareholderCompaniesIndexes || []
																).filter(
																	(i) =>
																		i.referenceNumber ===
																		matchingItem?.ReferenceNumber,
																);

																// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
																if (foundIndices.length > 0) {
																	return foundIndices.flatMap((foundIndex) => {
																		const dataEntry = data.find(
																			(item) =>
																				item.ReferenceNumber ===
																				matchingItem?.ReferenceNumber,
																		);
																		if (dataEntry) {
																			return (
																				dataEntry.VW_RPT_SUMMARY_PEL || []
																			).filter((_, secondIndex) =>
																				foundIndex.secondIndex.includes(
																					secondIndex,
																				),
																			);
																		}
																		return []; // Return an empty array if dataEntry is undefined
																	});
																}
																return []; // Return an empty array if no valid indices were found
															})() as unknown as Record<string, unknown>,
														},
														{
															sheetName: 'Per Fasilitas',
															data: (() => {
																const foundIndices = (
																	selectedShareholderCompaniesIndexes || []
																).filter(
																	(i) =>
																		i.referenceNumber ===
																		matchingItem?.ReferenceNumber,
																);

																// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
																if (foundIndices.length > 0) {
																	return foundIndices.flatMap((foundIndex) => {
																		const dataEntry = data.find(
																			(item) =>
																				item.ReferenceNumber ===
																				matchingItem?.ReferenceNumber,
																		);
																		if (dataEntry) {
																			return (
																				dataEntry.VW_RPT_DETAIL_FASILITAS || []
																			).filter((_, secondIndex) =>
																				foundIndex.secondIndex.includes(
																					secondIndex,
																				),
																			);
																		}
																		return []; // Return an empty array if dataEntry is undefined
																	});
																}
																return []; // Return an empty array if no valid indices were found
															})() as unknown as Record<string, unknown>,
														},
													);
												}}
											>
												<DownloadOutlined />
											</a>
										),
									},
									{
										title: 'Nama Debitur',
										render: (_, record) => record.namaDebitur ?? '-',
									},
									{
										title: 'Tanggal Update Terakhir',
										render: (_, record) =>
											formatCBASDate(record.tanggalUpdate) ?? '-',
									},
									{
										title: 'DOB',
										render: (_, record) =>
											formatCBASDate(record.tanggalLahir) ?? '-',
									},
									{ title: 'NIK', render: (_, record) => record.ktp ?? '-' },
									{
										title: 'Nama Ibu Kandung',
										render: (_, record) => record.mmname ?? '-',
									},
									{
										title: 'Jenis Kelamin',
										render: (_, record) => record.jenisKelaminKet ?? '-',
									},
									{
										title: 'Alamat',
										render: (_, record) => record.alamat ?? '-',
									},
									{
										title: 'Pekerjaan',
										render: (_, record) => record.pekerjaanKet ?? '-',
									},
									{
										title: 'Tempat Bekerja',
										render: (_, record) => record.tempatBekerja ?? '-',
									},
								]}
							/>
							<Divider />

							<Typography.Title level={3}>Ringkasan Fasilitas</Typography.Title>
							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={(() => {
									const foundIndices = (
										selectedShareholderCompaniesIndexes || []
									).filter(
										(i) => i.referenceNumber === matchingItem?.ReferenceNumber,
									);

									// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
									if (foundIndices.length > 0) {
										return foundIndices.flatMap((foundIndex) => {
											const dataEntry = data.find(
												(item) =>
													item.ReferenceNumber ===
													matchingItem?.ReferenceNumber,
											);
											if (dataEntry) {
												return (dataEntry.RPT_SUMMARY_COMPANY || []).filter(
													(_, secondIndex) =>
														foundIndex.secondIndex.includes(secondIndex),
												);
											}
											return []; // Return an empty array if dataEntry is undefined
										});
									}
									return []; // Return an empty array if no valid indices were found
								})().reduce<
									Array<{
										rowType: string;
										plafonEfektifKredit?: number;
										plafonEfektifSec?: number;
										plafonEfektifLc?: number;
										plafonEfektifBg?: number;
										plafonEfektifLainnya?: number;
										plafonEfektifTotal?: number;
										bakiDebetKredit?: number;
										bakiDebetSec?: number;
										bakiDebetLc?: number;
										bakiDebetBg?: number;
										bakiDebetLainnya?: number;
										bakiDebetTotal?: number;
									}>
								>((acc, record) => {
									const plafonEfektif = acc[0] || {
										rowType: 'plafonEfektif',
										plafonEfektifKredit: 0,
										plafonEfektifSec: 0,
										plafonEfektifLc: 0,
										plafonEfektifBg: 0,
										plafonEfektifLainnya: 0,
										plafonEfektifTotal: 0,
									};
									const bakiDebet = acc[1] || {
										rowType: 'bakiDebet',
										bakiDebetKredit: 0,
										bakiDebetSec: 0,
										bakiDebetLc: 0,
										bakiDebetBg: 0,
										bakiDebetLainnya: 0,
										bakiDebetTotal: 0,
									};

									return [
										{
											...plafonEfektif,
											plafonEfektifKredit:
												(Number(plafonEfektif.plafonEfektifKredit) || 0) +
												(Number(record.plafonEfektifKredit) || 0),

											plafonEfektifSec:
												(Number(plafonEfektif.plafonEfektifSec) || 0) +
												(Number(record.plafonEfektifSec) || 0),

											plafonEfektifLc:
												(Number(plafonEfektif.plafonEfektifLc) || 0) +
												(Number(record.plafonEfektifLc) || 0),

											plafonEfektifBg:
												(Number(plafonEfektif.plafonEfektifBg) || 0) +
												(Number(record.plafonEfektifBg) || 0),

											plafonEfektifLainnya:
												(Number(plafonEfektif.plafonEfektifLainnya) || 0) +
												(Number(record.plafonEfektifLainnya) || 0),

											plafonEfektifTotal:
												(Number(plafonEfektif.plafonEfektifTotal) || 0) +
												(Number(record.plafonEfektifTotal) || 0),
										},
										{
											...bakiDebet,
											bakiDebetKredit:
												(Number(bakiDebet.bakiDebetKredit) || 0) +
												(Number(record.bakiDebetKredit) || 0),

											bakiDebetSec:
												(Number(bakiDebet.bakiDebetSec) || 0) +
												(Number(record.bakiDebetSec) || 0),

											bakiDebetLc:
												(Number(bakiDebet.bakiDebetLc) || 0) +
												(Number(record.bakiDebetLc) || 0),

											bakiDebetBg:
												(Number(bakiDebet.bakiDebetBg) || 0) +
												(Number(record.bakiDebetBg) || 0),

											bakiDebetLainnya:
												(Number(bakiDebet.bakiDebetLainnya) || 0) +
												(Number(record.bakiDebetLainnya) || 0),

											bakiDebetTotal:
												(Number(bakiDebet.bakiDebetTotal) || 0) +
												(Number(record.bakiDebetTotal) || 0),
										},
									];
								}, [])}
								columns={[
									{
										title: 'Fasilitas',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? 'Plafon Efektif'
												: 'Baki Debet',
									},
									{
										title: 'Kredit/Pembiayaan (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(record.plafonEfektifKredit) ?? '-'
												: formatCurrency(record.bakiDebetKredit) ?? '-',
									},
									{
										title: 'Surat Berharga (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(record.plafonEfektifSec) ?? '-'
												: formatCurrency(record.bakiDebetSec) ?? '-',
									},
									{
										title: 'Irrevocable L/C (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(record.plafonEfektifLc) ?? '-'
												: formatCurrency(record.bakiDebetLc) ?? '-',
									},
									{
										title: 'Garansi yang diberikan (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(record.plafonEfektifBg) ?? '-'
												: formatCurrency(record.bakiDebetBg) ?? '-',
									},
									{
										title: 'Fasilitas Lain (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(record.plafonEfektifLainnya) ?? '-'
												: formatCurrency(record.bakiDebetLainnya) ?? '-',
									},
									{
										title: 'Total (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(record.plafonEfektifTotal) ?? '-'
												: formatCurrency(record.bakiDebetTotal) ?? '-',
									},
								]}
							/>

							<Divider />

							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={[
									[
										{
											title: 'Lembaga Pembiayaan',
											value: String(
												(
													(() => {
														const foundIndices = (
															selectedShareholderCompaniesIndexes || []
														).filter(
															(i) =>
																i.referenceNumber ===
																matchingItem?.ReferenceNumber,
														);

														// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
														if (foundIndices.length > 0) {
															return foundIndices.flatMap((foundIndex) => {
																const dataEntry = data.find(
																	(item) =>
																		item.ReferenceNumber ===
																		matchingItem?.ReferenceNumber,
																);
																if (dataEntry) {
																	return (
																		dataEntry.RPT_SUMMARY_COMPANY || []
																	).filter((_, secondIndex) =>
																		foundIndex.secondIndex.includes(
																			secondIndex,
																		),
																	);
																}
																return []; // Return an empty array if dataEntry is undefined
															});
														}
														return []; // Return an empty array if no valid indices were found
													})() || []
												).reduce(
													(acc, record) =>
														acc + (Number(record.krediturLp) || 0),
													0,
												),
											),
										},
										{
											title: 'Lainnya',
											value: String(
												(
													(() => {
														const foundIndices = (
															selectedShareholderCompaniesIndexes || []
														).filter(
															(i) =>
																i.referenceNumber ===
																matchingItem?.ReferenceNumber,
														);

														// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
														if (foundIndices.length > 0) {
															return foundIndices.flatMap((foundIndex) => {
																const dataEntry = data.find(
																	(item) =>
																		item.ReferenceNumber ===
																		matchingItem?.ReferenceNumber,
																);
																if (dataEntry) {
																	return (
																		dataEntry.RPT_SUMMARY_COMPANY || []
																	).filter((_, secondIndex) =>
																		foundIndex.secondIndex.includes(
																			secondIndex,
																		),
																	);
																}
																return []; // Return an empty array if dataEntry is undefined
															});
														}
														return []; // Return an empty array if no valid indices were found
													})() || []
												).reduce(
													(acc, record) =>
														acc + (Number(record.krediturLainnya) || 0),
													0,
												),
											),
										},
									],
									[
										{
											title: 'BPR/BPRS',
											value: String(
												(
													(() => {
														const foundIndices = (
															selectedShareholderCompaniesIndexes || []
														).filter(
															(i) =>
																i.referenceNumber ===
																matchingItem?.ReferenceNumber,
														);

														// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
														if (foundIndices.length > 0) {
															return foundIndices.flatMap((foundIndex) => {
																const dataEntry = data.find(
																	(item) =>
																		item.ReferenceNumber ===
																		matchingItem?.ReferenceNumber,
																);
																if (dataEntry) {
																	return (
																		dataEntry.RPT_SUMMARY_COMPANY || []
																	).filter((_, secondIndex) =>
																		foundIndex.secondIndex.includes(
																			secondIndex,
																		),
																	);
																}
																return []; // Return an empty array if dataEntry is undefined
															});
														}
														return []; // Return an empty array if no valid indices were found
													})() || []
												).reduce(
													(acc, record) =>
														acc + (Number(record.krediturBPR_S) || 0),
													0,
												),
											),
										},
										{
											title: 'Bank Umum',
											value: String(
												(
													(() => {
														const foundIndices = (
															selectedShareholderCompaniesIndexes || []
														).filter(
															(i) =>
																i.referenceNumber ===
																matchingItem?.ReferenceNumber,
														);

														// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
														if (foundIndices.length > 0) {
															return foundIndices.flatMap((foundIndex) => {
																const dataEntry = data.find(
																	(item) =>
																		item.ReferenceNumber ===
																		matchingItem?.ReferenceNumber,
																);
																if (dataEntry) {
																	return (
																		dataEntry.RPT_SUMMARY_COMPANY || []
																	).filter((_, secondIndex) =>
																		foundIndex.secondIndex.includes(
																			secondIndex,
																		),
																	);
																}
																return []; // Return an empty array if dataEntry is undefined
															});
														}
														return []; // Return an empty array if no valid indices were found
													})() || []
												).reduce(
													(acc, record) =>
														acc + (Number(record.krediturBankUmum) || 0),
													0,
												),
											),
										},
									],
								]}
								columns={[
									{
										title: 'Jumlah Kreditur',
										children: [
											{
												children: [
													{
														render: (_, record) => record[0].title,
														width: 100,
													},
													{
														render: (_, record) => record[0].value,
														width: 100,
													},
												],
											},
											{
												children: [
													{
														render: (_, record) => record[1].title,
														width: 100,
													},
													{
														render: (_, record) => record[1].value,
														width: 100,
													},
												],
											},
										],
									},
								]}
							/>

							<Divider />

							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={[
									[
										{
											title: 'Kualitas Terburuk',
											value:
												(
													(() => {
														const foundIndices = (
															selectedShareholderCompaniesIndexes || []
														).filter(
															(i) =>
																i.referenceNumber ===
																matchingItem?.ReferenceNumber,
														);

														// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
														if (foundIndices.length > 0) {
															return foundIndices.flatMap((foundIndex) => {
																const dataEntry = data.find(
																	(item) =>
																		item.ReferenceNumber ===
																		matchingItem?.ReferenceNumber,
																);
																if (dataEntry) {
																	return (
																		dataEntry.RPT_SUMMARY_COMPANY || []
																	).filter((_, secondIndex) =>
																		foundIndex.secondIndex.includes(
																			secondIndex,
																		),
																	);
																}
																return []; // Return an empty array if dataEntry is undefined
															});
														}
														return []; // Return an empty array if no valid indices were found
													})() || []
												).reduce((highest, record) => {
													const current = Number(record.kolekTerburuk) || 0;
													return current > highest ? current : highest;
												}, 0) ?? '-',
										},
										{
											title: 'Periode',
											value:
												formatCBASDate(
													(
														(() => {
															const foundIndices = (
																selectedShareholderCompaniesIndexes || []
															).filter(
																(i) =>
																	i.referenceNumber ===
																	matchingItem?.ReferenceNumber,
															);

															// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
															if (foundIndices.length > 0) {
																return foundIndices.flatMap((foundIndex) => {
																	const dataEntry = data.find(
																		(item) =>
																			item.ReferenceNumber ===
																			matchingItem?.ReferenceNumber,
																	);
																	if (dataEntry) {
																		return (
																			dataEntry.RPT_SUMMARY_COMPANY || []
																		).filter((_, secondIndex) =>
																			foundIndex.secondIndex.includes(
																				secondIndex,
																			),
																		);
																	}
																	return []; // Return an empty array if dataEntry is undefined
																});
															}
															return []; // Return an empty array if no valid indices were found
														})() || []
													).reduce((highest, record) => {
														const current = record.kolekBulanDataTerburuk || '';
														return current > highest ? current : highest;
													}, '') || '',
												) ?? '-',
										},
									],
									[
										{
											title: 'Kualitas Terakhir',
											value: (
												(() => {
													const foundIndices = (
														selectedShareholderCompaniesIndexes || []
													).filter(
														(i) =>
															i.referenceNumber ===
															matchingItem?.ReferenceNumber,
													);

													// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
													if (foundIndices.length > 0) {
														return foundIndices.flatMap((foundIndex) => {
															const dataEntry = data.find(
																(item) =>
																	item.ReferenceNumber ===
																	matchingItem?.ReferenceNumber,
															);
															if (dataEntry) {
																return (
																	dataEntry.RPT_SUMMARY_COMPANY || []
																).filter((_, secondIndex) =>
																	foundIndex.secondIndex.includes(secondIndex),
																);
															}
															return []; // Return an empty array if dataEntry is undefined
														});
													}
													return []; // Return an empty array if no valid indices were found
												})() || []
											).reduce(
												(highest, record) => {
													const current = record.tahunBulanKolAkhir || '';
													const currentNumber = Number(current.charAt(0)) || 0;
													return currentNumber > highest.number
														? { value: current, number: currentNumber }
														: { value: highest.value, number: highest.number };
												},
												{ value: '-', number: 0 },
											).value,
										},
										{
											title: 'Periode Terakhir',
											value:
												formatCBASDate(
													(
														(() => {
															const foundIndices = (
																selectedShareholderCompaniesIndexes || []
															).filter(
																(i) =>
																	i.referenceNumber ===
																	matchingItem?.ReferenceNumber,
															);

															// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
															if (foundIndices.length > 0) {
																return foundIndices.flatMap((foundIndex) => {
																	const dataEntry = data.find(
																		(item) =>
																			item.ReferenceNumber ===
																			matchingItem?.ReferenceNumber,
																	);
																	if (dataEntry) {
																		return (
																			dataEntry.RPT_SUMMARY_COMPANY || []
																		).filter((_, secondIndex) =>
																			foundIndex.secondIndex.includes(
																				secondIndex,
																			),
																		);
																	}
																	return []; // Return an empty array if dataEntry is undefined
																});
															}
															return []; // Return an empty array if no valid indices were found
														})() || []
													).reduce((highest, record) => {
														const current = record.tahunBulan01 || '';
														return current > highest ? current : highest;
													}, '') || '',
												) ?? '-',
										},
									],
								]}
								columns={[
									{
										title: 'Kolektibilitas',
										children: [
											{
												children: [
													{
														render: (_, record) => record[0].title,
														width: 100,
													},
													{
														render: (_, record) => record[0].value,
														width: 100,
													},
												],
											},
											{
												children: [
													{
														render: (_, record) => record[1].title,
														width: 100,
													},
													{
														render: (_, record) => record[1].value,
														width: 100,
													},
												],
											},
										],
									},
								]}
							/>
						</Fragment>
					) : (
						<Fragment>
							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={matchingItem?.RPT_SUMMARY_PERSONAL || []}
								columns={[
									{
										title: 'View Ideb',
										width: 100,
										align: 'center',
										render: (_, record) => (
											<a
												href={CBAS_URL}
												target="_blank"
											>
												<FileSearchOutlined
													style={
														!('reffnumber' in record)
															? { color: '#bababa' }
															: {}
													}
												/>
											</a>
										),
									},
									{
										title: 'Download File',
										render: () => (
											<a
												href="#"
												onClick={async (e) => {
													e.preventDefault();
													downloadExcel(
														matchingItem?.RPT_SUMMARY_PERSONAL[0].namaDebitur ??
															'',
														{
															sheetName: 'Per Pelapor',
															data: matchingItem?.VW_RPT_SUMMARY_PEL as unknown as Record<
																string,
																unknown
															>,
														},
														{
															sheetName: 'Per Pemegang Saham',
															data: matchingItem?.VW_RPT_DETAIL_FASILITAS as unknown as Record<
																string,
																unknown
															>,
														},
													);
												}}
											>
												<DownloadOutlined />
											</a>
										),
									},
									{
										title: 'Nama Debitur',
										render: (_, record) => record.namaDebitur ?? '-',
									},
									{
										title: 'Tanggal Update Terakhir',
										render: (_, record) =>
											formatCBASDate(record.tanggalUpdate) ?? '-',
									},
									{
										title: 'DOB',
										render: (_, record) =>
											formatCBASDate(record.tanggalLahir) ?? '-',
									},
									{ title: 'NIK', render: (_, record) => record.ktp ?? '-' },
									{
										title: 'Nama Ibu Kandung',
										render: (_, record) => record.mmname ?? '-',
									},
									{
										title: 'Jenis Kelamin',
										render: (_, record) => record.jenisKelaminKet ?? '-',
									},
									{
										title: 'Alamat',
										render: (_, record) => record.alamat ?? '-',
									},
									{
										title: 'Pekerjaan',
										render: (_, record) => record.pekerjaanKet ?? '-',
									},
									{
										title: 'Tempat Bekerja',
										render: (_, record) => record.tempatBekerja ?? '-',
									},
								]}
							/>
							<Divider />

							<Typography.Title level={3}>Ringkasan Fasilitas</Typography.Title>
							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={(matchingItem?.RPT_SUMMARY_PERSONAL || []).flatMap(
									(record) => [
										{ ...record, rowType: 'plafonEfektif' },
										{ ...record, rowType: 'bakiDebet' },
									],
								)}
								columns={[
									{
										title: 'Fasilitas',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? 'Plafon Efektif'
												: 'Baki Debet',
									},
									{
										title: 'Kredit/Pembiayaan (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(Number(record.plafonEfektifKredit)) ??
													'-'
												: formatCurrency(Number(record.bakiDebetKredit)) ?? '-',
									},
									{
										title: 'Surat Berharga (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(Number(record.plafonEfektifSec)) ?? '-'
												: formatCurrency(Number(record.bakiDebetSec)) ?? '-',
									},
									{
										title: 'Irrevocable L/C (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(Number(record.plafonEfektifLc)) ?? '-'
												: formatCurrency(Number(record.bakiDebetLc)) ?? '-',
									},
									{
										title: 'Garansi yang diberikan (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(Number(record.plafonEfektifBg)) ?? '-'
												: formatCurrency(Number(record.bakiDebetBg)) ?? '-',
									},
									{
										title: 'Fasilitas Lain (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(Number(record.plafonEfektifLainnya)) ??
													'-'
												: formatCurrency(Number(record.bakiDebetLainnya)) ??
													'-',
									},
									{
										title: 'Total (dalam IDR)',
										render: (_, record) =>
											record.rowType === 'plafonEfektif'
												? formatCurrency(Number(record.plafonEfektifTotal)) ??
													'-'
												: formatCurrency(Number(record.bakiDebetTotal)) ?? '-',
									},
								]}
							/>

							<Divider />

							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={[
									[
										{
											title: 'Lembaga Pembiayaan',
											value:
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]?.krediturLp ??
												'0',
										},
										{
											title: 'Lainnya',
											value:
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
													?.krediturLainnya ?? '0',
										},
									],
									[
										{
											title: 'BPR/BPRS',
											value:
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
													?.krediturBPR_S ?? '0',
										},
										{
											title: 'Bank Umum',
											value:
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
													?.krediturBankUmum ?? '0',
										},
									],
								]}
								columns={[
									{
										title: 'Jumlah Kreditur',
										children: [
											{
												children: [
													{
														render: (_, record) => record[0].title,
														width: 100,
													},
													{
														render: (_, record) => record[0].value,
														width: 100,
													},
												],
											},
											{
												children: [
													{
														render: (_, record) => record[1].title,
														width: 100,
													},
													{
														render: (_, record) => record[1].value,
														width: 100,
													},
												],
											},
										],
									},
								]}
							/>

							<Divider />

							<Table
								scroll={{ x: 'max-content' }}
								style={{ marginBottom: 20 }}
								pagination={false}
								dataSource={[
									[
										{
											title: 'Kualitas Terburuk',
											value:
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
													?.kolekTerburuk ?? '-',
										},
										{
											title: 'Periode',
											value:
												formatCBASDate(
													matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
														?.kolekBulanDataTerburuk || '',
												) ?? '-',
										},
									],
									[
										{
											title: 'Kualitas Terakhir',
											value:
												matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
													?.tahunBulanKolAkhir ?? '-',
										},
										{
											title: 'Periode Terakhir',
											value:
												formatCBASDate(
													matchingItem?.RPT_SUMMARY_PERSONAL?.[0]
														?.tahunBulan01 || '',
												) ?? '-',
										},
									],
								]}
								columns={[
									{
										title: 'Kolektibilitas',
										children: [
											{
												children: [
													{
														render: (_, record) => record[0].title,
														width: 100,
													},
													{
														render: (_, record) => record[0].value,
														width: 100,
													},
												],
											},
											{
												children: [
													{
														render: (_, record) => record[1].title,
														width: 100,
													},
													{
														render: (_, record) => record[1].value,
														width: 100,
													},
												],
											},
										],
									},
								]}
							/>
						</Fragment>
					)}

					<Divider />

					<Typography.Title
						level={3}
						onClick={() =>
							setSelectedDetail({
								referenceNumber: matchingItem?.ReferenceNumber ?? '',
								detail: 'shareholder',
								type: 'reporter',
							})
						}
					>
						<span style={{ marginRight: 10 }}>View Detail</span>
						<ArrowRightOutlined style={{ color: '#1557BC' }} />
					</Typography.Title>

					{/* <Divider />

						<Typography.Title
							level={3}
							onClick={() =>
								setSelectedDetail({
									index: dataIndex,
									detail: 'shareholder',
									type: 'facility',
								})
							}
						>
							<span style={{ marginRight: 10 }}>View Detail per Fasilitas</span>
							<ArrowRightOutlined style={{ color: '#1557BC' }} />
						</Typography.Title> */}
				</Fragment>
			),
		};
	});

	if (!data) return <FleetTitle>No Data</FleetTitle>;

	const companyNotFound = data[0]?.RPT_SUMMARY_COMPANY.length < 1;

	// CHECKPOINT: DEBITUR-CHECK
	return (
		<Fragment>
			<FleetTitle>View Company</FleetTitle>

			{companyNotFound ? (
				<Alert
					message="Proses pengecekan SLIK sudah dilakukan dan data debitur tidak ditemukan."
					type="warning"
					className="custom-alert-info"
					style={{ marginBottom: 20 }}
					showIcon
				/>
			) : (
				<Fragment>
					<Typography.Title level={3}>Summary</Typography.Title>
					<Table
						scroll={{ x: 'max-content' }}
						style={{ marginBottom: 20 }}
						pagination={false}
						dataSource={(data[0]?.RPT_SUMMARY_COMPANY || []).filter(
							(_, index) => selectedCompaniesIndexes.includes(index),
						)}
						columns={[
							{
								title: 'View Ideb',
								width: 100,
								align: 'center',
								render: (_, record) => (
									<a
										href={CBAS_URL}
										target="_blank"
									>
										<FileSearchOutlined
											style={
												!('reffnumber' in record) ? { color: '#bababa' } : {}
											}
										/>
									</a>
								),
							},
							{
								title: 'Download File',
								render: () => (
									<a
										href="#"
										onClick={async (e) => {
											e.preventDefault();
											downloadExcel(
												(() => {
													const selectedCompanies =
														data?.[0]?.RPT_SUMMARY_COMPANY.filter((_, i) =>
															selectedCompaniesIndexes.includes(i),
														);
													return `${selectedCompanies.map((i) => `${i.namaDebitur}; `)}`;
												})(),
												{
													sheetName: 'Per Pelapor',
													data: companyReporterDataSource as unknown as Record<
														string,
														unknown
													>,
												},
												{
													sheetName: 'Per Fasilitas',
													data: companyFacilityDataSource as unknown as Record<
														string,
														unknown
													>,
												},
											);
										}}
									>
										<DownloadOutlined />
									</a>
								),
							},
							{
								title: 'Nama Debitur',
								render: (_, record) => record.namaDebitur ?? '-',
							},
							{
								title: 'Tanggal Update Terakhir',
								render: (_, record) =>
									formatCBASDate(record.tanggalUpdate) ?? '-',
							},
							{
								title: 'DOB',
								render: (_, record) =>
									formatCBASDate(record.tanggalLahir) ?? '-',
							},
							{ title: 'NIK', render: (_, record) => record.ktp ?? '-' },
							{
								title: 'Nama Ibu Kandung',
								render: (_, record) => record.mmname ?? '-',
							},
							{
								title: 'Jenis Kelamin',
								render: (_, record) => record.jenisKelaminKet ?? '-',
							},
							{ title: 'Alamat', render: (_, record) => record.alamat ?? '-' },
							{
								title: 'Pekerjaan',
								render: (_, record) => record.pekerjaanKet ?? '-',
							},
							{
								title: 'Tempat Bekerja',
								render: (_, record) => record.tempatBekerja ?? '-',
							},
						]}
					/>
					<Divider />

					<Typography.Title level={3}>Ringkasan Fasilitas</Typography.Title>
					<Table
						scroll={{ x: 'max-content' }}
						style={{ marginBottom: 20 }}
						pagination={false}
						dataSource={(data[0]?.RPT_SUMMARY_COMPANY || [])
							.filter((_, index) => selectedCompaniesIndexes.includes(index))
							.reduce<
								Array<{
									rowType: string;
									plafonEfektifKredit?: string;
									plafonEfektifSec?: string;
									plafonEfektifLc?: string;
									plafonEfektifBg?: string;
									plafonEfektifLainnya?: string;
									plafonEfektifTotal?: string;
									bakiDebetKredit?: string;
									bakiDebetSec?: string;
									bakiDebetLc?: string;
									bakiDebetBg?: string;
									bakiDebetLainnya?: string;
									bakiDebetTotal?: string;
								}>
							>((acc, record) => {
								const plafonEfektif = acc[0] || {
									rowType: 'plafonEfektif',
									plafonEfektifKredit: '0',
									plafonEfektifSec: '0',
									plafonEfektifLc: '0',
									plafonEfektifBg: '0',
									plafonEfektifLainnya: '0',
									plafonEfektifTotal: '0',
								};
								const bakiDebet = acc[1] || {
									rowType: 'bakiDebet',
									bakiDebetKredit: '0',
									bakiDebetSec: '0',
									bakiDebetLc: '0',
									bakiDebetBg: '0',
									bakiDebetLainnya: '0',
									bakiDebetTotal: '0',
								};

								return [
									{
										...plafonEfektif,
										plafonEfektifKredit: String(
											(Number(plafonEfektif.plafonEfektifKredit) || 0) +
												(Number(record.plafonEfektifKredit) || 0),
										),
										plafonEfektifSec: String(
											(Number(plafonEfektif.plafonEfektifSec) || 0) +
												(Number(record.plafonEfektifSec) || 0),
										),
										plafonEfektifLc: String(
											(Number(plafonEfektif.plafonEfektifLc) || 0) +
												(Number(record.plafonEfektifLc) || 0),
										),
										plafonEfektifBg: String(
											(Number(plafonEfektif.plafonEfektifBg) || 0) +
												(Number(record.plafonEfektifBg) || 0),
										),
										plafonEfektifLainnya: String(
											(Number(plafonEfektif.plafonEfektifLainnya) || 0) +
												(Number(record.plafonEfektifLainnya) || 0),
										),
										plafonEfektifTotal: String(
											(Number(plafonEfektif.plafonEfektifTotal) || 0) +
												(Number(record.plafonEfektifTotal) || 0),
										),
									},
									{
										...bakiDebet,
										bakiDebetKredit: String(
											(Number(bakiDebet.bakiDebetKredit) || 0) +
												(Number(record.bakiDebetKredit) || 0),
										),
										bakiDebetSec: String(
											(Number(bakiDebet.bakiDebetSec) || 0) +
												(Number(record.bakiDebetSec) || 0),
										),
										bakiDebetLc: String(
											(Number(bakiDebet.bakiDebetLc) || 0) +
												(Number(record.bakiDebetLc) || 0),
										),
										bakiDebetBg: String(
											(Number(bakiDebet.bakiDebetBg) || 0) +
												(Number(record.bakiDebetBg) || 0),
										),
										bakiDebetLainnya: String(
											(Number(bakiDebet.bakiDebetLainnya) || 0) +
												(Number(record.bakiDebetLainnya) || 0),
										),
										bakiDebetTotal: String(
											(Number(bakiDebet.bakiDebetTotal) || 0) +
												(Number(record.bakiDebetTotal) || 0),
										),
									},
								];
							}, [])}
						columns={[
							{
								title: 'Fasilitas',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? 'Plafon Efektif'
										: 'Baki Debet',
							},
							{
								title: 'Kredit/Pembiayaan (dalam IDR)',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? formatCurrency(Number(record.plafonEfektifKredit)) ?? '-'
										: formatCurrency(Number(record.bakiDebetKredit)) ?? '-',
							},
							{
								title: 'Surat Berharga (dalam IDR)',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? formatCurrency(Number(record.plafonEfektifSec)) ?? '-'
										: formatCurrency(Number(record.bakiDebetSec)) ?? '-',
							},
							{
								title: 'Irrevocable L/C (dalam IDR)',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? formatCurrency(Number(record.plafonEfektifLc)) ?? '-'
										: formatCurrency(Number(record.bakiDebetLc)) ?? '-',
							},
							{
								title: 'Garansi yang diberikan (dalam IDR)',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? formatCurrency(Number(record.plafonEfektifBg)) ?? '-'
										: formatCurrency(Number(record.bakiDebetBg)) ?? '-',
							},
							{
								title: 'Fasilitas Lain (dalam IDR)',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? formatCurrency(Number(record.plafonEfektifLainnya)) ?? '-'
										: formatCurrency(Number(record.bakiDebetLainnya)) ?? '-',
							},
							{
								title: 'Total (dalam IDR)',
								render: (_, record) =>
									record.rowType === 'plafonEfektif'
										? formatCurrency(Number(record.plafonEfektifTotal)) ?? '-'
										: formatCurrency(Number(record.bakiDebetTotal)) ?? '-',
							},
						]}
					/>

					<Divider />

					<Table
						scroll={{ x: 'max-content' }}
						style={{ marginBottom: 20 }}
						pagination={false}
						dataSource={[
							[
								{
									title: 'Lembaga Pembiayaan',
									value: String(
										(data[0]?.RPT_SUMMARY_COMPANY || [])
											.filter((_, index) =>
												selectedCompaniesIndexes.includes(index),
											)
											.reduce(
												(acc, record) => acc + (Number(record.krediturLp) || 0),
												0,
											),
									),
								},
								{
									title: 'Lainnya',
									value: String(
										(data[0]?.RPT_SUMMARY_COMPANY || [])
											.filter((_, index) =>
												selectedCompaniesIndexes.includes(index),
											)
											.reduce(
												(acc, record) =>
													acc + (Number(record.krediturLainnya) || 0),
												0,
											),
									),
								},
							],
							[
								{
									title: 'BPR/BPRS',
									value: String(
										(data[0]?.RPT_SUMMARY_COMPANY || [])
											.filter((_, index) =>
												selectedCompaniesIndexes.includes(index),
											)
											.reduce(
												(acc, record) =>
													acc + (Number(record.krediturBPR_S) || 0),
												0,
											),
									),
								},
								{
									title: 'Bank Umum',
									value: String(
										(data[0]?.RPT_SUMMARY_COMPANY || [])
											.filter((_, index) =>
												selectedCompaniesIndexes.includes(index),
											)
											.reduce(
												(acc, record) =>
													acc + (Number(record.krediturBankUmum) || 0),
												0,
											),
									),
								},
							],
						]}
						columns={[
							{
								title: 'Jumlah Kreditur',
								children: [
									{
										children: [
											{ render: (_, record) => record[0].title, width: 100 },
											{ render: (_, record) => record[0].value, width: 100 },
										],
									},
									{
										children: [
											{ render: (_, record) => record[1].title, width: 100 },
											{ render: (_, record) => record[1].value, width: 100 },
										],
									},
								],
							},
						]}
					/>

					<Divider />

					<Table
						scroll={{ x: 'max-content' }}
						style={{ marginBottom: 20 }}
						pagination={false}
						dataSource={[
							[
								{
									title: 'Kualitas Terburuk',
									value:
										data[0]?.RPT_SUMMARY_COMPANY?.filter((_, index) =>
											selectedCompaniesIndexes.includes(index),
										).reduce((highest, record) => {
											const current = Number(record.kolekTerburuk) || 0;
											return current > highest ? current : highest;
										}, 0) ?? '-',
								},
								{
									title: 'Periode',
									value:
										formatCBASDate(
											data[0]?.RPT_SUMMARY_COMPANY?.filter((_, index) =>
												selectedCompaniesIndexes.includes(index),
											).reduce((highest, record) => {
												const current = record.kolekBulanDataTerburuk || '';
												return current > highest ? current : highest;
											}, '') || '',
										) ?? '-',
								},
							],
							[
								{
									title: 'Kualitas Terakhir',
									value: data[0]?.RPT_SUMMARY_COMPANY?.filter((_, index) =>
										selectedCompaniesIndexes.includes(index),
									).reduce(
										(highest, record) => {
											const current = record.tahunBulanKolAkhir || '';
											const currentNumber = Number(current.charAt(0)) || 0;
											return currentNumber > highest.number
												? { value: current, number: currentNumber }
												: { value: highest.value, number: highest.number };
										},
										{ value: '-', number: 0 },
									).value,
								},
								{
									title: 'Periode Terakhir',
									value:
										formatCBASDate(
											data[0]?.RPT_SUMMARY_COMPANY?.filter((_, index) =>
												selectedCompaniesIndexes.includes(index),
											).reduce((highest, record) => {
												const current = record.tahunBulan01 || '';
												return current > highest ? current : highest;
											}, '') || '',
										) ?? '-',
								},
							],
						]}
						columns={[
							{
								title: 'Kolektibilitas',
								children: [
									{
										children: [
											{ render: (_, record) => record[0].title, width: 100 },
											{ render: (_, record) => record[0].value, width: 100 },
										],
									},
									{
										children: [
											{ render: (_, record) => record[1].title, width: 100 },
											{ render: (_, record) => record[1].value, width: 100 },
										],
									},
								],
							},
						]}
					/>

					<Divider />

					<Collapse
						onChange={(key) => setActiveKey(key as unknown as string)}
						activeKey={activeKey}
						items={[
							{
								key: 'reporter',
								label: 'View Detail Per Pelapor',
								children: (
									<Table
										scroll={{ x: 'max-content' }}
										style={{ marginBottom: 20 }}
										pagination={false}
										dataSource={companyReporterDataSource}
										columns={[
											{
												title: 'Pelapor',
												render: (_, record) => record.ljkKet ?? '-',
											},
											{
												title: 'Tanggal Update Terakhir',
												render: (_, record) =>
													formatCBASDate(record.tanggalUpdate) ?? '-',
											},
											{
												title: 'Tanggal Awal Kredit',
												render: (_, record) =>
													formatCBASDate(record.tanggalAwalKredit) ?? '-',
											},
											{
												title: 'Tanggal Jatuh Tempo',
												render: (_, record) =>
													formatCBASDate(record.tanggalJatuhTempo) ?? '-',
											},
											{
												title: 'Bunga (%)',
												children: [
													{
														title: 'Terendah',
														render: (_, record) => record.sukuBungamin ?? '-',
													},
													{
														title: 'Maksimal',
														render: (_, record) => record.sukuBungamax ?? '-',
													},
													{
														title: 'Rata-Rata',
														render: (_, record) => record.sukuBungaavg ?? '-',
													},
												],
											},
											{
												title: 'Limit Kredit',
												render: (_, record) => record.plafonAwal ?? '-',
											},
											{
												title: 'Limit Skedul',
												render: (_, record) => record.plafon ?? '-',
											},
											{
												title: 'Baki Debet',
												render: (_, record) => record.bakiDebet ?? '-',
											},
											{
												title: 'Tunggakan Pokok',
												render: (_, record) => record.tunggakanPokok ?? '-',
											},
											{
												title: 'Tunggakan Bunga',
												render: (_, record) => record.tunggakanBunga ?? '-',
											},
											{
												title: 'Denda',
												render: (_, record) => record.denda ?? '-',
											},
											{
												title: 'Total Angsuran',
												render: (_, record) => record.angsuran ?? '-',
											},
											{
												title: 'Tenor',
												children: [
													{
														title: 'Terendah',
														render: (_, record) => record.tenormin ?? '-',
													},
													{
														title: 'Maksimal',
														render: (_, record) => record.tenormax ?? '-',
													},
													{
														title: 'Rata-Rata',
														render: (_, record) => record.tenoravg ?? '-',
													},
												],
											},
											{
												title: 'Jenis Fasilitas',
												children: [
													{
														title: 'Investasi',
														render: (_, record) =>
															record.jenisPenggunaan2 ?? '-',
													},
													{
														title: 'Modal Kerja',
														render: (_, record) =>
															record.jenisPenggunaan1 ?? '-',
													},
													{
														title: 'Konsumsi',
														render: (_, record) =>
															record.jenisPenggunaan3 ?? '-',
													},
												],
											},
											{
												title: 'Kolektibilitas Terburuk 24 Bulan Terakhir',
												render: (_, record) => record.maxkol ?? '-',
											},
											{
												title: 'Overdue Terburuk 24 Bulan Terakhir',
												render: (_, record) => record.maxdpd ?? '-',
											},
											{
												title: 'Kolektibilitas Bulan Terakhir',
												render: (_, record) => record.tahunBulanKolAkhir ?? '-',
											},
											{
												title: 'Overdue Bulan Terakhir',
												render: (_, record) =>
													record.jumlahHariTunggakan ?? '-',
											},
											{
												title: 'Tanggal Restrukturisasi Akhir',
												render: (_, record) =>
													formatCBASDate(record.tanggalRestrukturisasiAkhir) ??
													'-',
											},
										]}
									/>
								),
							},
							{
								key: 'facility',
								label: 'View Detail Per Fasilitas',
								children: (
									<Table
										scroll={{ x: 'max-content' }}
										style={{ marginBottom: 20 }}
										pagination={false}
										dataSource={companyFacilityDataSource}
										columns={[
											{
												title: 'Pelapor',
												render: (_, record) => record.ljkKet ?? '-',
											},
											{
												title: 'Tanggal Update',
												render: (_, record) =>
													formatCBASDate(record.tanggalUpdate) ?? '-',
											},
											{
												title: 'Sifat Kredit/Pembiayaan',
												render: (_, record) => record.sifatKreditKet ?? '-',
											},
											{
												title: 'Jenis Kredit/Pembiayaan',
												render: (_, record) => record.jenisKreditKet ?? '-',
											},
											{
												title: 'Frekuensi Perpanjang Kredit/Pembiayaan',
												render: (_, record) => record.Perpanjang_Kredit ?? '-',
											},
											{
												title: 'No Akad Terakhir',
												render: (_, record) => record.noAkadAkhir ?? '-',
											},
											{
												title: 'Tanggal Akad Terakhir',
												render: (_, record) =>
													formatCBASDate(record.tanggalAkadAkhir) ?? '-',
											},
											{
												title: 'Tanggal Awal Kredit',
												render: (_, record) =>
													formatCBASDate(record.tanggalAwalKredit) ?? '-',
											},
											{
												title: 'Tanggal Mulai',
												render: (_, record) =>
													formatCBASDate(record.tanggalMulai) ?? '-',
											},
											{
												title: 'Tanggal Jatuh Tempo',
												render: (_, record) =>
													formatCBASDate(record.tanggalJatuhTempo) ?? '-',
											},
											{
												title: 'Tenor',
												render: (_, record) => record.tenor ?? '-',
											},
											{
												title: 'Jenis Penggunaan',
												render: (_, record) => record.jenisPenggunaanKet ?? '-',
											},
											{
												title: 'Sektor Ekonomi',
												render: (_, record) => record.sektorEkonomiKet ?? '-',
											},
											{
												title: 'Kab/Kota Lokasi Projek',
												render: (_, record) => record.lokasiProyekKet ?? '-',
											},
											{
												title: 'Valuta',
												render: (_, record) => record.kodeValuta ?? '-',
											},
											{
												title: 'Suku Bunga/Imbalan',
												render: (_, record) => record.sukuBunga ?? '-',
											},
											{
												title: 'Keterangan',
												render: (_, record) => record.keterangan ?? '-',
											},
											{
												title: 'Kualitas (Terburuk)',
												render: (_, record) =>
													record.kolektibilitasMax24 ?? '-',
											},
											{
												title: 'Jumlah Hari Tunggakan (Terburuk)',
												render: (_, record) => record.DPDMax24 ?? '-',
											},
											{
												title: 'Kualitas (Akhir)',
												render: (_, record) => record.tahunBulanKolAkhir ?? '-',
											},
											{
												title: 'Jumlah Hari Tunggakan (Akhir)',
												render: (_, record) =>
													record.jumlahHariTunggakan ?? '-',
											},
											{
												title: 'Plafon Awal',
												render: (_, record) =>
													formatCurrency(Number(record.plafonAwal)) ?? '-',
											},
											{
												title: 'Plafon',
												render: (_, record) =>
													formatCurrency(Number(record.plafon)) ?? '-',
											},
											{
												title: 'Baki Debet',
												render: (_, record) =>
													formatCurrency(Number(record.bakiDebet)) ?? '-',
											},
											{
												title: 'Kelonggaran Tarik',
												render: (_, record) =>
													formatCurrency(Number(record.Kelonggaran)) ?? '-',
											},
											{
												title: 'Angsuran Pokok Per Bulan',
												render: (_, record) =>
													formatCurrency(Number(record.angsuranpokok)) ?? '-',
											},
											{
												title: 'Bunga Per Bulan',
												render: (_, record) =>
													formatCurrency(Number(record.bunga)) ?? '-',
											},
											{
												title: 'Angsuran Per Bulan',
												render: (_, record) =>
													formatCurrency(Number(record.angsuranbulan)) ?? '-',
											},
											{
												title: 'Tunggakan Pokok',
												render: (_, record) =>
													formatCurrency(Number(record.tunggakanPokok)) ?? '-',
											},
											{
												title: 'Tunggakan Bunga',
												render: (_, record) =>
													formatCurrency(Number(record.tunggakanBunga)) ?? '-',
											},
											{
												title: 'Frekuensi Tunggakan',
												render: (_, record) => record.frekuensiTunggakan ?? '-',
											},
											{
												title: 'Denda',
												render: (_, record) =>
													formatCurrency(Number(record.denda)) ?? '-',
											},
											{
												title: 'Frekuensi Restrukturisasi',
												render: (_, record) =>
													record.frekuensiRestrukturisasi ?? '-',
											},
											{
												title: 'Tanggal Restrukturisasi Akhir',
												render: (_, record) =>
													formatCBASDate(record.tanggalRestrukturisasiAkhir) ??
													'-',
											},
											{
												title: 'Cara Restrukturisasi',
												render: (_, record) =>
													record.kodeCaraRestrukturisasi ?? '-',
											},
											{
												title: 'Kondisi',
												render: (_, record) => record.kondisiKet ?? '-',
											},
											{
												title: 'Tanggal Kondisi',
												render: (_, record) =>
													formatCBASDate(record.tanggalKondisi) ?? '-',
											},
										]}
									/>
								),
							},
						]}
					/>

					<Divider />
				</Fragment>
			)}

			<FleetTitle>View Personal</FleetTitle>
			<Tabs
				items={[
					{
						label: 'Pengurus',
						key: 'management',
						children: (
							<Fragment>
								{managementSummaries?.map((item, index) => (
									<Collapse
										key={index}
										items={[item]}
									/>
								))}
							</Fragment>
						),
					},
					{
						label: 'Pemegang Saham',
						key: 'shareholder',
						children: (
							<Fragment>
								{shareholderSummaries?.map((item, index) => (
									<Collapse
										key={index}
										items={[item]}
									/>
								))}
							</Fragment>
						),
					},
				]}
				style={{ marginBottom: 20, marginTop: 40 }}
			/>

			{/* MODALS */}

			<FleetModal
				open={!!selectedDetail}
				width={940}
				cancelText="BACK"
				buttonPosition="end"
				closable={false}
				okButtonProps={{ hidden: true }}
				onCancel={() => setSelectedDetail(undefined)}
			>
				<FleetTitle>
					Detail{' '}
					{selectedDetail?.detail === 'management'
						? 'Pengurus'
						: 'Pemegang Saham'}{' '}
					{selectedDetail && selectedDetail.referenceNumber}
				</FleetTitle>

				{selectedShareholderCompaniesIndexes.some(
					(i) => i.referenceNumber === selectedDetail?.referenceNumber,
				) ? (
					<Table
						scroll={{ x: 'max-content' }}
						style={{ marginBottom: 20 }}
						pagination={false}
						dataSource={(() => {
							const foundIndices = (
								selectedShareholderCompaniesIndexes || []
							).filter(
								(i) => i.referenceNumber === selectedDetail?.referenceNumber,
							);

							// If foundIndices is not empty, get the RPT_SUMMARY_COMPANY for each valid index
							if (foundIndices.length > 0) {
								return foundIndices.flatMap((foundIndex) => {
									const dataEntry = data.find(
										(item) =>
											item.ReferenceNumber === foundIndex.referenceNumber,
									);
									if (dataEntry) {
										return (dataEntry.RPT_SUMMARY_COMPANY || []).filter(
											(_, secondIndex) =>
												foundIndex.secondIndex.includes(secondIndex),
										);
									}
									return []; // Return an empty array if dataEntry is undefined
								});
							}
							return []; // Return an empty array if no valid indices were found
						})()}
						columns={[
							{
								title: 'Nama Debitur',
								render: (_, record) => record.namaDebitur ?? '-',
							},
							{
								title: 'Tanggal Update Terakhir',
								render: (_, record) =>
									formatCBASDate(record.tanggalUpdate) ?? '-',
							},
							{
								title: 'DOB',
								render: (_, record) =>
									formatCBASDate(record.tanggalLahir) ?? '-',
							},
							{ title: 'NIK', render: (_, record) => record.ktp ?? '-' },
							{
								title: 'Nama Ibu Kandung',
								render: (_, record) => record.mmname ?? '-',
							},
							{
								title: 'Jenis Kelamin',
								render: (_, record) => record.jenisKelaminKet ?? '-',
							},
							{ title: 'Alamat', render: (_, record) => record.alamat ?? '-' },
							{
								title: 'Pekerjaan',
								render: (_, record) => record.pekerjaanKet ?? '-',
							},
							{
								title: 'Tempat Bekerja',
								render: (_, record) => record.tempatBekerja ?? '-',
							},
						]}
					/>
				) : (
					<Table
						scroll={{ x: 'max-content' }}
						style={{ marginBottom: 20 }}
						pagination={false}
						dataSource={
							selectedDetail?.referenceNumber
								? data.find(
										(item) =>
											item.ReferenceNumber === selectedDetail?.referenceNumber,
									)?.RPT_SUMMARY_PERSONAL
								: ({} as SlikRobotikDetail[number]['RPT_SUMMARY_PERSONAL'])
						}
						columns={[
							{
								title: 'Nama Debitur',
								render: (_, record) => record.namaDebitur,
							},
							{
								title: 'Tanggal Update Terakhir',
								render: (_, record) =>
									formatCBASDate(record.tanggalUpdate) ?? '-',
							},
							{
								title: 'DOB',
								render: (_, record) =>
									formatCBASDate(record.tanggalLahir) ?? '-',
							},
							{ title: 'NIK', render: (_, record) => record.ktp ?? '-' },
							{
								title: 'Nama Ibu Kandung',
								render: (_, record) => record.mmname ?? '-',
							},
							{
								title: 'Jenis Kelamin',
								render: (_, record) => record.jenisKelaminKet ?? '-',
							},

							{ title: 'Alamat', render: (_, record) => record.alamat ?? '-' },
						]}
					/>
				)}

				<Collapse
					onChange={(key) => setActiveKey(key as unknown as string)}
					activeKey={activeKey}
					items={[
						{
							key: 'reporter',
							label: 'View Detail Per Pelapor',
							children: (
								<Table
									scroll={{ x: 'max-content' }}
									style={{ marginBottom: 20 }}
									pagination={false}
									dataSource={
										selectedDetail?.referenceNumber
											? (() => {
													// Get Selected shareholders
													const shareholderIndices =
														selectedShareholderCompaniesIndexes.filter(
															(i) =>
																i.referenceNumber ===
																selectedDetail?.referenceNumber,
														);

													if (shareholderIndices.length > 0) {
														// Return all VW_RPT_SUMMARY_PEL for all selected shareholders

														return shareholderIndices.flatMap((foundIndex) => {
															const selectedData = data.find(
																(item) =>
																	item.ReferenceNumber ===
																	foundIndex.referenceNumber,
															);

															const dataEntry = selectedData;
															// const secondIndex = foundIndex.secondIndex;
															if (dataEntry) {
																return (
																	dataEntry.VW_RPT_SUMMARY_PEL || []
																).filter(
																	(i) =>
																		i.identitasnew ===
																		dataEntry?.RPT_SUMMARY_COMPANY[
																			foundIndex.secondIndex[0]
																		].npwp,
																);
															}
															return []; // Return an empty array if dataEntry is undefined
														});
													} else {
														return data.find(
															(item) =>
																item.ReferenceNumber ===
																selectedDetail?.referenceNumber,
														)?.VW_RPT_SUMMARY_PEL;
													}
												})()
											: ([
													{},
												] as SlikRobotikDetail[number]['VW_RPT_SUMMARY_PEL'])
									}
									columns={[
										{
											title: 'Pelapor',
											render: (_, record) => record.ljkKet ?? '-',
										},
										{
											title: 'Tanggal Update Terakhir',
											render: (_, record) =>
												formatCBASDate(record.tanggalUpdate) ?? '-',
										},
										{
											title: 'Tanggal Awal Kredit',
											render: (_, record) =>
												formatCBASDate(record.tanggalAwalKredit) ?? '-',
										},
										{
											title: 'Tanggal Jatuh Tempo',
											render: (_, record) =>
												formatCBASDate(record.tanggalJatuhTempo) ?? '-',
										},
										{
											title: 'Bunga (%)',
											children: [
												{
													title: 'Terendah',
													render: (_, record) => record.sukuBungamin ?? '-',
												},
												{
													title: 'Maksimal',
													render: (_, record) => record.sukuBungamax ?? '-',
												},
												{
													title: 'Rata-Rata',
													render: (_, record) => record.sukuBungaavg ?? '-',
												},
											],
										},
										{
											title: 'Limit Kredit',
											render: (_, record) => record.plafonAwal ?? '-',
										},
										{
											title: 'Limit Skedul',
											render: (_, record) => record.plafon ?? '-',
										},
										{
											title: 'Baki Debet',
											render: (_, record) => record.bakiDebet ?? '-',
										},
										{
											title: 'Tunggakan Pokok',
											render: (_, record) => record.tunggakanPokok ?? '-',
										},
										{
											title: 'Tunggakan Bunga',
											render: (_, record) => record.tunggakanBunga ?? '-',
										},
										{
											title: 'Denda',
											render: (_, record) => record.denda ?? '-',
										},
										{
											title: 'Total Angsuran',
											render: (_, record) => record.angsuran ?? '-',
										},
										{
											title: 'Tenor',
											children: [
												{
													title: 'Terendah',
													render: (_, record) => record.tenormin ?? '-',
												},
												{
													title: 'Maksimal',
													render: (_, record) => record.tenormax ?? '-',
												},
												{
													title: 'Rata-Rata',
													render: (_, record) => record.tenoravg ?? '-',
												},
											],
										},
										{
											title: 'Jenis Fasilitas',
											children: [
												{
													title: 'Investasi',
													render: (_, record) => record.jenisPenggunaan2 ?? '-',
												},
												{
													title: 'Modal Kerja',
													render: (_, record) => record.jenisPenggunaan1 ?? '-',
												},
												{
													title: 'Konsumsi',
													render: (_, record) => record.jenisPenggunaan3 ?? '-',
												},
											],
										},
										{
											title: 'Kolektibilitas Terburuk 24 Bulan Terakhir',
											render: (_, record) => record.maxkol ?? '-',
										},
										{
											title: 'Overdue Terburuk 24 Bulan Terakhir',
											render: (_, record) => record.maxdpd ?? '-',
										},
										{
											title: 'Kolektibilitas Bulan Terakhir',
											render: (_, record) => record.tahunBulanKolAkhir ?? '-',
										},
										{
											title: 'Overdue Bulan Terakhir',
											render: (_, record) => record.jumlahHariTunggakan ?? '-',
										},
										{
											title: 'Tanggal Restrukturisasi Akhir',
											render: (_, record) =>
												formatCBASDate(record.tanggalRestrukturisasiAkhir) ??
												'-',
										},
									]}
								/>
							),
						},
						{
							key: 'facility',
							label: 'View Detail Per Fasilitas',
							children: (
								<Table
									scroll={{ x: 'max-content' }}
									style={{ marginBottom: 20 }}
									pagination={false}
									dataSource={
										selectedDetail?.referenceNumber
											? (() => {
													const selectedData = data.find(
														(item) =>
															item.ReferenceNumber ===
															selectedDetail?.referenceNumber,
													);
													const shareholderIndices =
														selectedShareholderCompaniesIndexes.filter(
															(i) =>
																i.referenceNumber ===
																selectedDetail?.referenceNumber,
														);

													if (shareholderIndices.length > 0) {
														return shareholderIndices.flatMap((foundIndex) => {
															const dataEntry = selectedData;
															if (dataEntry) {
																return (
																	dataEntry.VW_RPT_DETAIL_FASILITAS || []
																).filter(
																	(i) =>
																		i.identitasnew ===
																		selectedData?.RPT_SUMMARY_COMPANY[
																			foundIndex.secondIndex[0]
																		].npwp,
																);
															}
															return []; // Return an empty array if dataEntry is undefined
														});
													} else {
														return selectedData?.VW_RPT_DETAIL_FASILITAS;
													}
												})()
											: ([
													{},
												] as SlikRobotikDetail[number]['VW_RPT_DETAIL_FASILITAS'])
									}
									columns={[
										{
											title: 'Pelapor',
											render: (_, record) => record.ljkKet ?? '-',
										},
										{
											title: 'Tanggal Update',
											render: (_, record) =>
												formatCBASDate(record.tanggalUpdate) ?? '-',
										},
										{
											title: 'Sifat Kredit/Pembiayaan',
											render: (_, record) => record.sifatKreditKet ?? '-',
										},
										{
											title: 'Jenis Kredit/Pembiayaan',
											render: (_, record) => record.jenisKreditKet ?? '-',
										},
										{
											title: 'Frekuensi Perpanjang Kredit/Pembiayaan',
											render: (_, record) => record.Perpanjang_Kredit ?? '-',
										},
										{
											title: 'No Akad Terakhir',
											render: (_, record) => record.noAkadAkhir ?? '-',
										},
										{
											title: 'Tanggal Akad Terakhir',
											render: (_, record) =>
												formatCBASDate(record.tanggalAkadAkhir) ?? '-',
										},
										{
											title: 'Tanggal Awal Kredit',
											render: (_, record) =>
												formatCBASDate(record.tanggalAwalKredit) ?? '-',
										},
										{
											title: 'Tanggal Mulai',
											render: (_, record) =>
												formatCBASDate(record.tanggalMulai) ?? '-',
										},
										{
											title: 'Tanggal Jatuh Tempo',
											render: (_, record) =>
												formatCBASDate(record.tanggalJatuhTempo) ?? '-',
										},
										{
											title: 'Tenor',
											render: (_, record) => record.tenor ?? '-',
										},
										{
											title: 'Jenis Penggunaan',
											render: (_, record) => record.jenisPenggunaanKet ?? '-',
										},
										{
											title: 'Sektor Ekonomi',
											render: (_, record) => record.sektorEkonomiKet ?? '-',
										},
										{
											title: 'Kab/Kota Lokasi Projek',
											render: (_, record) => record.lokasiProyekKet ?? '-',
										},
										{
											title: 'Valuta',
											render: (_, record) => record.kodeValuta ?? '-',
										},
										{
											title: 'Suku Bunga/Imbalan',
											render: (_, record) => record.sukuBunga ?? '-',
										},
										{
											title: 'Keterangan',
											render: (_, record) => record.keterangan ?? '-',
										},
										{
											title: 'Kualitas (Terburuk)',
											render: (_, record) => record.kolektibilitasMax24 ?? '-',
										},
										{
											title: 'Jumlah Hari Tunggakan (Terburuk)',
											render: (_, record) => record.DPDMax24 ?? '-',
										},
										{
											title: 'Kualitas (Akhir)',
											render: (_, record) => record.tahunBulanKolAkhir ?? '-',
										},
										{
											title: 'Jumlah Hari Tunggakan (Akhir)',
											render: (_, record) => record.jumlahHariTunggakan ?? '-',
										},
										{
											title: 'Plafon Awal',
											render: (_, record) => record.plafonAwal ?? '-',
										},
										{
											title: 'Plafon',
											render: (_, record) =>
												formatCurrency(Number(record.plafon)) ?? '-',
										},
										{
											title: 'Baki Debet',
											render: (_, record) =>
												formatCurrency(Number(record.bakiDebet)) ?? '-',
										},
										{
											title: 'Kelonggaran Tarik',
											render: (_, record) =>
												formatCurrency(Number(record.Kelonggaran)) ?? '-',
										},
										{
											title: 'Angsuran Pokok Per Bulan',
											render: (_, record) =>
												formatCurrency(Number(record.angsuranpokok)) ?? '-',
										},
										{
											title: 'Bunga Per Bulan',
											render: (_, record) =>
												formatCurrency(Number(record.bunga)) ?? '-',
										},
										{
											title: 'Angsuran Per Bulan',
											render: (_, record) =>
												formatCurrency(Number(record.angsuranbulan)) ?? '-',
										},
										{
											title: 'Tunggakan Pokok',
											render: (_, record) =>
												formatCurrency(Number(record.tunggakanPokok)) ?? '-',
										},
										{
											title: 'Tunggakan Bunga',
											render: (_, record) =>
												formatCurrency(Number(record.tunggakanBunga)) ?? '-',
										},
										{
											title: 'Frekuensi Tunggakan',
											render: (_, record) => record.frekuensiTunggakan ?? '-',
										},
										{
											title: 'Denda',
											render: (_, record) =>
												formatCurrency(Number(record.denda)) ?? '-',
										},
										{
											title: 'Frekuensi Restrukturisasi',
											render: (_, record) =>
												record.frekuensiRestrukturisasi ?? '-',
										},
										{
											title: 'Tanggal Restrukturisasi Akhir',
											render: (_, record) =>
												formatCBASDate(record.tanggalRestrukturisasiAkhir) ??
												'-',
										},
										{
											title: 'Cara Restrukturisasi',
											render: (_, record) =>
												record.kodeCaraRestrukturisasi ?? '-',
										},
										{
											title: 'Kondisi',
											render: (_, record) => record.kondisiKet ?? '-',
										},
										{
											title: 'Tanggal Kondisi',
											render: (_, record) =>
												formatCBASDate(record.tanggalKondisi) ?? '-',
										},
										// {
										// 	title: 'Jenis Agunan',
										// 	render: (_, record) => record.collateralType ?? '-',
										// },
										// {
										// 	title: 'Nilai Agunan',
										// 	render: (_, record) => record.collateralValue ?? '-',
										// },
										// {
										// 	title: 'Nama Pemilik Agunan',
										// 	render: (_, record) => record.collateralOwnerName ?? '-',
										// },
									]}
								/>
							),
						},
					]}
				/>
			</FleetModal>
		</Fragment>
	);
};

export default AnalisaSLIKFormRobotik;
